import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import type {
  ConditionDto,
  EditActionDto,
  NewAutoTransactionRuleDto,
  UpdateAutoTransactionRuleDto,
  AutoTransactionRuleDto,
} from "../../api/types";
import type { StoredTransaction } from "../../logic/derivedAccount";
import {
  createAutoTransactionRule,
  getAutoTransactionRule,
  updateAutoTransactionRule,
} from "../../api/auto-transactions";
import { errorText } from "../../api/common";
import {
  Alert,
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DialogCloseButton } from "../../components/DialogCloseButton";
import { useRegistry } from "../../context/RegistryContext";
import { getInitialStateFromTransaction } from "../../logic/auto-transaction-form-logic";
import { AutoTransactionMatchesDialog } from "../../components/AutoTransactionMatchesDialog";
import {
  ActionListEditor,
  ConditionEditor,
  joinLabels,
  retiredActionLabels,
  retiredConditionLabels,
  unsetActionLabels,
  unsetConditionLabels,
} from "../../components/RuleEditor";

export type AutoTransactionFormProps = {
  initialTransaction?: StoredTransaction;
  ruleId?: string; // New prop for loading a specific rule
  onClose?: () => void;
  onSuccess?: () => void;
};

export const AutoTransactionForm = ({
  initialTransaction,
  ruleId,
  onClose,
  onSuccess,
}: AutoTransactionFormProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id: paramId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const transaction =
    initialTransaction ||
    (location.state?.transaction as StoredTransaction | undefined);
  // The admin list's "Duplicate" action navigates here with a `fromRule`
  // state payload — a snapshot of the source rule (already renamed to
  // "Copy of …"). We treat it as a new rule, so the form POSTs on save.
  const fromRule = location.state?.fromRule as
    | AutoTransactionRuleDto
    | undefined;
  const { sourceMap, groupMap, categoryMap, expenseTypeMap } = useRegistry();

  const id = ruleId || paramId;
  const isEdit = !!id;

  const initial = getInitialStateFromTransaction(
    isEdit ? undefined : transaction,
    {
      sourceByName: sourceMap,
      groupByName: groupMap,
      categoryByName: categoryMap,
      expenseTypeByName: expenseTypeMap,
    },
  );

  const [name, setName] = useState(fromRule?.name ?? initial.name);
  const [description, setDescription] = useState(fromRule?.description ?? "");
  const [priority, setPriority] = useState(fromRule?.priority ?? 0);
  const [enabled, setEnabled] = useState(fromRule?.enabled ?? true);
  const [condition, setCondition] = useState<ConditionDto>(
    fromRule?.condition ?? initial.condition,
  );
  const [actions, setActions] = useState<EditActionDto[]>(
    fromRule?.actions ?? initial.actions,
  );
  // Opens the "matching transactions" preview. The dialog reads the current
  // `condition` at open time; closing it before editing further is part of
  // the flow.
  const [matchesOpen, setMatchesOpen] = useState(false);
  // Every failure here has to reach the screen: the app is used on mobile,
  // where there is no console to read a swallowed error from.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Saving after a failed load would PUT the form's blank defaults over the
  // stored rule — the one way to lose a rule the server could not read.
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchRule = async () => {
      if (id) {
        try {
          const rule = await getAutoTransactionRule(id);
          if (!active) return;
          if (rule) {
            setName(rule.name);
            setDescription(rule.description || "");
            setPriority(rule.priority);
            setEnabled(rule.enabled);
            setCondition(rule.condition);
            setActions(rule.actions);
          }
        } catch (error) {
          if (!active) return;
          setLoadFailed(true);
          setErrorMessage(`Failed to load the rule: ${errorText(error)}`);
        }
      }
    };

    if (isEdit) {
      void fetchRule();
    }
    return () => {
      active = false;
    };
  }, [id, isEdit]);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const ruleData = {
      name,
      description: description || null,
      priority,
      enabled,
      condition,
      actions,
    };

    const unset = [
      ...unsetConditionLabels(condition),
      ...unsetActionLabels(actions),
    ];
    if (unset.length > 0) {
      setErrorMessage(
        `Pick a value for ${joinLabels(unset)} before saving the rule.`,
      );
      return;
    }
    // Same shape as the check above: the backend would load the rule but
    // report it dead, so refuse here and say which reference to remove.
    const retired = [
      ...retiredConditionLabels(condition),
      ...retiredActionLabels(actions),
    ];
    if (retired.length > 0) {
      setErrorMessage(
        `Remove ${joinLabels(retired)} before saving the rule — the field no longer exists.`,
      );
      return;
    }

    setErrorMessage(null);
    try {
      if (isEdit) {
        await updateAutoTransactionRule(
          id!,
          ruleData as UpdateAutoTransactionRuleDto,
        );
      } else {
        await createAutoTransactionRule(ruleData as NewAutoTransactionRuleDto);
      }
      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/admin/auto-transactions");
      }
    } catch (error) {
      setErrorMessage(`Failed to save the rule: ${errorText(error)}`);
    }
  };

  return (
    <>
      <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
        <Paper sx={{ p: 3 }}>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {isEdit
                ? "Edit Auto Transaction Rule"
                : "New Auto Transaction Rule"}
            </Typography>
            {onClose && <DialogCloseButton onClick={onClose} />}
          </Stack>
          {errorMessage && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setErrorMessage(null)}
            >
              {errorMessage}
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Priority"
                  value={priority}
                  onChange={(e) => setPriority(parseInt(e.target.value))}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                    />
                  }
                  label="Enabled"
                />
              </Stack>

              <Box>
                <Typography variant="h6" gutterBottom>
                  Condition
                </Typography>
                <ConditionEditor
                  condition={condition}
                  onChange={setCondition}
                  isMobile={isMobile}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setMatchesOpen(true)}
                  sx={{ mt: 1 }}
                >
                  Show matching transactions
                </Button>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom>
                  Actions
                </Typography>
                <ActionListEditor
                  actions={actions}
                  onChange={setActions}
                  isMobile={isMobile}
                />
              </Box>

              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  onClick={() =>
                    onClose ? onClose() : navigate("/admin/auto-transactions")
                  }
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loadFailed}
                >
                  Save
                </Button>
              </Stack>
            </Stack>
          </form>
        </Paper>
      </Box>
      <AutoTransactionMatchesDialog
        open={matchesOpen}
        onClose={() => setMatchesOpen(false)}
        condition={condition}
      />
    </>
  );
};
